import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { getPublicSpeciesCareGuide, getAllSpeciesForCareGuides, getProductsByGenus } from '../services/api';
import { getSpeciesImage, getSpeciesGridImage, resolveCredit, retryThenPlaceholder } from '../utils/imageResolver';
import { NO_PHOTO_SRC } from '../constants/noPhoto';
import { createSpeciesSlug } from '../utils/slugUtils';
import { buildAssetUrl } from '../utils/imageResolver';
import ImageAttribution from '../components/media/ImageAttribution';
import PhotoCarousel from '../components/common/PhotoCarousel';
import FroggySpinner from '../components/common/FroggySpinner';
import { Helmet } from 'react-helmet-async';
import { buildHowToSchema, buildFAQPageSchema } from '../utils/seoSchema';
import { getFAQsForSpecies } from '../constants/careGuideFAQs';
import ProductCard from '../components/Products/ProductCard';
import Breadcrumbs from '../components/Navigation/Breadcrumbs';
import { formatSpeciesName } from '../utils/scientificName';
import { anchorMarginFor } from '../utils/topBar';
import {
  readGuideHeadings, headingIdAt, bodyPhotosFrom, planGuideBody,
} from '../utils/careGuideSections';

// The gap kept between the top bar's bottom edge — or the top of the
// viewport, once the bar has scrolled away — and a heading a Table of
// Contents link jumps to (Cowork run 44: headings landed at y=0, under the
// sticky navbar while it is still pinned).
export const GUIDE_ANCHOR_GAP = 16;

/**
 * The scroll margin ``target`` needs so it stops just below whatever of the
 * top bar will be covering the viewport WHERE IT LANDS
 * (`utils/topBar.topBarOverlapAt`). Landing with the gap alone leaves no
 * bar over the heading → the gap alone (the bar has scrolled away — no
 * blank band where no bar is). Otherwise the bar's full height plus the
 * gap (``topBarOverlapAt(0)`` is the bar in its natural place): it is
 * still pinned there, or partly pushed off, and either way the heading
 * clears it.
 */
export const guideAnchorMargin = (target, doc = document, win = window) => (
  // The one implementation (`utils/topBar.anchorMarginFor`), shared with
  // every other deep link.
  anchorMarginFor(target, GUIDE_ANCHOR_GAP, doc, win));

/**
 * A TOC link's click: set the margin for ITS target, then let the anchor go.
 * Set as the page's `--ff-guide-anchor-offset` (which every anchor target
 * reads), never on the heading itself: the hash change re-renders the
 * guide, and a heading that re-mounts drops an inline style before the
 * browser has scrolled to it (measured in Chromium).
 */
/**
 * A DIRECT HASH LOAD (a shared "/care-guides/x#user-content-feeding"
 * link, review of run 44): the guide renders after an API fetch, so the
 * browser's own jump finds no element and nothing scrolls. Once the guide
 * is on screen, the hash's target gets the SAME margin a TOC click gives
 * it and is scrolled to — instantly under reduced motion. Returns whether
 * a target was found.
 */
export const scrollToGuideHash = (hash, doc = document, win = window) => {
  if (!hash || hash.length < 2) return false;
  let id;
  try { id = decodeURIComponent(hash.slice(1)); } catch (e) { id = hash.slice(1); }
  const target = doc.getElementById(id);
  const page = target && target.closest('.care-guide-page');
  if (!page) return false;
  page.style.setProperty('--ff-guide-anchor-offset', `${guideAnchorMargin(target, doc, win)}px`);
  if (typeof target.scrollIntoView !== 'function') return true;
  let reduce = false;
  try {
    reduce = !!(win.matchMedia && win.matchMedia('(prefers-reduced-motion: reduce)').matches);
  } catch (e) { reduce = false; }
  target.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
  return true;
};

const aimTocTarget = (event) => {
  const href = event.currentTarget.getAttribute('href') || '';
  if (!href.startsWith('#')) return;
  const target = document.getElementById(decodeURIComponent(href.slice(1)));
  const page = target && target.closest('.care-guide-page');
  if (page) {
    page.style.setProperty('--ff-guide-anchor-offset', `${guideAnchorMargin(target)}px`);
  }
};

// Heading renderers that carry the id the Table of Contents links to.
// The guide's headings are read ONCE (`readGuideHeadings`, which the TOC
// and the section split also read); a rendered heading finds its id by
// the line it stands on in the whole guide — this block's `offset` plus
// the node's own position — so a TOC link and its heading cannot be
// derived two ways. Before 2026-10-12 the headings carried no id at all
// and every TOC link went nowhere.
const headingComponentsFor = (full, offset, idsByLine) => {
  const make = (Tag) => {
    const GuideHeading = ({ node, children, ...props }) => {
      const at = node?.position?.start?.offset;
      const id = Number.isInteger(at) ? headingIdAt(full, idsByLine, offset + at) : undefined;
      return <Tag {...props} id={id}>{children}</Tag>;
    };
    return GuideHeading;
  };
  return { h1: make('h1'), h2: make('h2'), h3: make('h3'), h4: make('h4'), h5: make('h5'), h6: make('h6') };
};

/** One block of the guide body, rendered with ReactMarkdown (never raw HTML). */
const GuideMarkdown = ({ source, offset = 0, full, idsByLine }) => {
  const components = useMemo(
    () => headingComponentsFor(full, offset, idsByLine),
    [full, offset, idsByLine],
  );
  return <ReactMarkdown components={components}>{source}</ReactMarkdown>;
};

/**
 * A photo between two sections of a long guide. A photo that fails to
 * load is retried once and then HIDDEN — a "no photo" tile in the middle
 * of the text tells the reader nothing.
 */
const GuidePhoto = ({ photo, alt }) => {
  const [hidden, setHidden] = useState(false);
  if (hidden) return null;
  const onError = (e) => {
    const img = e.target;
    if (img && !img.dataset.ffRetried && img.src) {
      img.dataset.ffRetried = '1';
      const sep = img.src.includes('?') ? '&' : '?';
      img.src = `${img.src}${sep}ffr=${Date.now()}`;
      return;
    }
    setHidden(true);
  };
  // The credit reads as it does in the sidebar carousel: the attribution
  // text when there is one, else the source/license line.
  let caption = null;
  if (photo.creditInfo) {
    caption = <ImageAttribution asset={photo.creditInfo} variant="caption" />;
  } else if (photo.credit) {
    caption = (
      <small className="ff-image-attribution ff-image-attribution--caption">{photo.credit}</small>
    );
  }
  return (
    <figure className="care-guide-photo" data-guide-photo={photo.number}>
      <img
        className="care-guide-photo__img"
        src={photo.src}
        alt={alt}
        loading="lazy"
        decoding="async"
        width="1200"
        height="800"
        onError={onError}
      />
      {caption && <figcaption className="care-guide-photo__credit">{caption}</figcaption>}
    </figure>
  );
};

const SpeciesCareGuide = () => {
  const { speciesSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // The slug whose guide has had its ONE hash scroll (`scrollToGuideHash`).
  // Set the first time that guide is on screen, hash or none, so a later
  // hash change (a TOC click — the browser jumps for it) or any re-render
  // never scrolls the page again.
  const hashScrolledFor = useRef(null);
  const [careGuide, setCareGuide] = useState(null);
  const [species, setSpecies] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);

  useEffect(() => {
    const loadCareGuide = async () => {
      try {
        setLoading(true);
        setError(null);

        // First, get the species by slug to find the ID
        const allSpeciesResponse = await getAllSpeciesForCareGuides();

        // Handle the API response structure
        const speciesList = Array.isArray(allSpeciesResponse?.results) ? allSpeciesResponse.results
                         : Array.isArray(allSpeciesResponse?.data?.results) ? allSpeciesResponse.data.results
                         : Array.isArray(allSpeciesResponse) ? allSpeciesResponse
                         : [];

        // Use imported createSpeciesSlug utility
        const foundSpecies = speciesList.find(s => createSpeciesSlug(s) === speciesSlug);

        if (!foundSpecies) {
          setError('Species not found');
          return;
        }

        // Check if the species has any guide content using the care_guide_available flag
        if (!foundSpecies.care_guide_available) {
          setError('Care guide not available for this species');
          return;
        }

        setSpecies(foundSpecies);

        // Fetch related products for this species — unless we keep it but
        // do not sell it (I-03 close 1c): the guide stays, the "Shop"
        // cards do not. The server's genus endpoint excludes it too; this
        // just skips a request whose answer is known.
        if (foundSpecies.not_sold_here) {
          setRelatedProducts([]);
        } else {
          try {
            const productsResponse = await getProductsByGenus(foundSpecies.genus);
            const rp = Array.isArray(productsResponse) ? productsResponse : [];
            setRelatedProducts(rp);
          } catch (productsError) {
            console.warn('Failed to fetch related products:', productsError);
            setRelatedProducts([]);
          }
        }

        // Get the full care guide data (with any server-rendered HTML)
        try {
          const guideData = await getPublicSpeciesCareGuide(foundSpecies.id);
          // #132d (Cowork prod sweep 2026-08-19): guide bodies begin with
          // their own "# <species> Care Guide" H1 while this page renders
          // its own H1 — two H1s per page and the heading leaked into
          // index excerpts. Strip the body's leading H1; everything else
          // (H2 sections, TOC) is untouched.
          let bodyMd = (guideData?.care_guide_md || '').replace(/^\s*#\s[^\n]*\n+/, '');
          // #133a (Cowork recheck 2026-08-20): the house guide template
          // ALSO opens with its own "## Overview" section, which this
          // page already renders from overview_md — readers saw the same
          // paragraph twice. When overview_md exists, drop the body's
          // leading Overview section (heading + prose, up to the next
          // H2). Overview-only bodies with no overview_md keep theirs.
          if ((guideData?.overview_md || '').trim()) {
            bodyMd = bodyMd.replace(/^\s*##\s+Overview\s*\n[\s\S]*?(?=\n##\s)/, '');
          }
          setCareGuide({
            ...guideData,
            care_guide_md: bodyMd,
          });
        } catch (guideError) {
          // Fallback with empty content if care guide endpoint fails
          console.warn('Care guide endpoint failed:', guideError);
          setCareGuide({
            overview_md: '',
            care_guide_md: 'Care guide content is being loaded...',
            species_id: foundSpecies.id,
            updated_at: foundSpecies.care_guide_updated_at || foundSpecies.updated_at
          });
        }
      } catch (err) {
        console.error('Error loading care guide:', err);
        setError('Failed to load care guide');
      } finally {
        setLoading(false);
      }
    };

    if (speciesSlug) {
      loadCareGuide();
    }
  }, [speciesSlug]);

  useEffect(() => {
    // The guide on screen must be THIS slug's: on an in-app move to
    // another guide the old one is still drawn for a render.
    if (loading || !careGuide || !species || createSpeciesSlug(species) !== speciesSlug
        || hashScrolledFor.current === speciesSlug) return;
    hashScrolledFor.current = speciesSlug;
    scrollToGuideHash(location.hash);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, careGuide, species, speciesSlug]);

  // Extract first paragraph for meta description
  const getExcerpt = (markdown) => {
    if (!markdown) return '';
    const firstParagraph = markdown.split('\n\n')[0];
    return firstParagraph.replace(/[#*_`]/g, '').substring(0, 160);
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="text-center py-5">
          <FroggySpinner size="lg" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="alert alert-warning">
          <h4>Care Guide Not Found</h4>
          <p>{error}</p>
          <button
            type="button"
            className="ff-btn ff-btn--primary"
            onClick={() => navigate('/care-guides')}
          >
            View All Care Guides
          </button>
        </div>
      </div>
    );
  }

  if (!careGuide || !species) {
    return null;
  }

  // Extract common name using multiple fallback patterns
  const commonName = species?.common_names?.[0]?.name || species?.common_names?.[0] || species?.common_name || species?.commonName || '';

  const speciesDisplayName = formatSpeciesName(species);

  // SEO-optimized title format: "{Scientific Name} – {Common Name} Care Guide | Feeling Froggy"
  const pageTitle = commonName
    ? `${speciesDisplayName} – ${commonName} Care Guide | Feeling Froggy`
    : `${speciesDisplayName} Care Guide | Feeling Froggy`;

  // SEO-optimized description: "Learn how to care for {Common Name} ({Scientific Name}): setup, feeding, humidity, and health tips."
  const baseDescription = getExcerpt(careGuide.overview_md || careGuide.care_guide_md);
  const description = commonName && baseDescription
    ? `Learn how to care for ${commonName} (${speciesDisplayName}): ${baseDescription}`
    : baseDescription || `Complete care guide for ${speciesDisplayName} including habitat setup, feeding, temperature, humidity, and health tips.`;

  // ONE reading of the guide's headings: the TOC (its H2s), the ids the
  // rendered headings carry, and the section split all come from it.
  const guideHeadings = readGuideHeadings(careGuide.care_guide_md);
  const headingIds = new Map(guideHeadings.map((h) => [h.offset, h.id]));
  const toc = guideHeadings.filter((h) => h.level === 2 && h.text);

  // Build HowTo schema for enhanced search results
  const speciesImageUrl = species?.imageUrl || getSpeciesGridImage(species) || getSpeciesImage(species);
  const fullImageUrl = speciesImageUrl ? buildAssetUrl(speciesImageUrl) : 'https://feelingfroggy.llc/logos/ff_original_96px.png';

  const howToSchema = buildHowToSchema({
    commonName,
    scientificName: speciesDisplayName,
    description,
    imageUrl: fullImageUrl,
    markdown: careGuide.care_guide_md || '',
    url: `https://feelingfroggy.llc/care-guides/${speciesSlug}`
  });

  // Build FAQPage schema for enhanced search results
  const speciesFAQs = getFAQsForSpecies(speciesSlug);
  const faqPageSchema = buildFAQPageSchema({
    scientificName: speciesDisplayName,
    commonName,
    faqs: speciesFAQs
  });

  // Conservative hero image deduplication
  const normalizePath = (u) => {
    if (!u) return '';
    return (u || '').split('?')[0].replace(/^https?:\/\/[^/]+/, '').replace(/\/$/, '');
  };

  const heroPath = normalizePath(fullImageUrl);

  // Extract product array from relatedProducts
  const rp = Array.isArray(relatedProducts) ? relatedProducts : [];

  // Normalize strings for comparison (case-insensitive, quote-insensitive, whitespace-trimmed)
  const norm = (s) => {
    return (s || '').toString().toLowerCase().replace(/['''"]/g, '').trim();
  };

  // Helper: check if product image matches hero (for deduplication)
  const isHeroImage = (product) => {
    const img = normalizePath(product?.images?.[0]?.url || product?.image_url || '');
    return heroPath && img && heroPath === img;
  };

  // Check if product exactly matches care guide (genus + species + locality)
  const isExactMatch = (product) => {
    if (!product?.species) return false;
    return (
      norm(product.species.genus) === norm(species?.genus) &&
      norm(product.species.species) === norm(species?.species) &&
      norm(product.species.locality) === norm(species?.locality)
    );
  };

  // Check if product is same genus (for similar frogs section)
  const isSameGenus = (product) => {
    if (!product?.species) return false;
    return norm(product.species.genus) === norm(species?.genus);
  };

  // Check if product has inventory available
  const hasInventory = (product) => {
    return product?.is_for_sale && (product?.count ?? 0) > 0;
  };

  // Featured: exact match (genus + species + locality) with inventory
  const exactMatches = rp.filter((p) => isExactMatch(p) && hasInventory(p));
  const sortedExact = exactMatches.sort((a, b) => {
    // In-stock first, then lowest price
    const aStock = a.is_for_sale && a.count > 0;
    const bStock = b.is_for_sale && b.count > 0;
    if (bStock !== aStock) {
      return bStock ? 1 : -1;
    }
    return (a.price ?? 1e9) - (b.price ?? 1e9);
  });
  const featured = sortedExact[0] || null;

  // Similar frogs: same genus, different locality, has inventory, not hero image
  const genusMatches = rp.filter((p) =>
    isSameGenus(p) &&
    !isExactMatch(p) &&
    hasInventory(p) &&
    !isHeroImage(p)
  );
  const sortedGenus = genusMatches.sort((a, b) => {
    // In-stock first, then lowest price
    const aStock = a.is_for_sale && a.count > 0;
    const bStock = b.is_for_sale && b.count > 0;
    if (bStock !== aStock) {
      return bStock ? 1 : -1;
    }
    return (a.price ?? 1e9) - (b.price ?? 1e9);
  });
  const genusOthers = sortedGenus.slice(0, 12);

  // Long guides carry photos between their sections (2026-10-12). The
  // sidebar's carousel leads with the gallery's first photo, so the body
  // starts at the second; when a featured product card takes the sidebar,
  // the gallery is shown nowhere else and the body uses all of it.
  const bodyPhotos = bodyPhotosFrom(careGuide?.gallery, {
    sidebarShowsGallery: !featured,
    resolveUrl: buildAssetUrl,
  });
  const bodyPlan = planGuideBody(careGuide.care_guide_md, bodyPhotos);
  const photoAlt = (n) => (commonName
    ? `${commonName} (${speciesDisplayName}) — photo ${n}`
    : `${speciesDisplayName} — photo ${n}`);

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href={`https://feelingfroggy.llc/care-guides/${speciesSlug}`} />

        {/* Article Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": commonName ? `${commonName} (${speciesDisplayName}) Care Guide` : `${speciesDisplayName} Care Guide`,
            "description": description,
            "author": {
              "@type": "Organization",
              "name": "Feeling Froggy"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Feeling Froggy",
              "logo": {
                "@type": "ImageObject",
                "url": "https://feelingfroggy.llc/logos/ff_original_96px.png"
              }
            },
            "datePublished": careGuide.updated_at,
            "dateModified": careGuide.updated_at,
            "about": {
              "@type": "Thing",
              "name": speciesDisplayName
            }
          })}
        </script>

        {/* HowTo Schema - Enhanced search results for "how to care for" queries */}
        {howToSchema && (
          <script type="application/ld+json">
            {JSON.stringify(howToSchema)}
          </script>
        )}

        {/* FAQPage Schema - Enhanced search results for frequently asked questions */}
        {faqPageSchema && (
          <script type="application/ld+json">
            {JSON.stringify(faqPageSchema)}
          </script>
        )}
      </Helmet>

      <div className="page-container care-guide-page">
        <Breadcrumbs items={[
          { name: "Home", url: "/" },
          { name: "Care Guides", url: "/care-guides" },
          { name: commonName || speciesDisplayName, url: `/care-guides/${speciesSlug}` }
        ]} />

        <header className="mb-4">
          <div className="row">
            <div className="col-12">
              <h1 className="ff-heading-h1">{speciesDisplayName}</h1>
              {commonName && (
                <p className="lead text-muted mb-0">{commonName}</p>
              )}
            </div>
          </div>
        </header>

        <div className="row">
          <div className="col-lg-8">
            {/* 2026-07-18 XSS fix: care_guide_html + overview_html
                fields are backend-generated via markdown.markdown()
                with NO bleach sanitization (verified in
                store/public_views.py:515,527). Raw <script> tags or
                onerror=... attributes in operator-authored markdown
                pass straight through to the customer browser. Switched
                to rendering markdown client-side via ReactMarkdown,
                which strips dangerous HTML by default. Backend
                sanitization is a separate defense-in-depth fix for
                other consumers (MCP, JSON-LD structured data). */}
            {careGuide.overview_md && (
              <section className="mb-5">
                <h2>Overview</h2>
                <div className="care-guide-content">
                  <ReactMarkdown>{careGuide.overview_md}</ReactMarkdown>
                </div>
              </section>
            )}

            {careGuide.care_guide_md && (
              <section className="care-guide-content">
                {bodyPlan ? bodyPlan.map((block, i) => (block.kind === 'photo' ? (
                  <GuidePhoto
                    key={`photo-${block.photo.number}`}
                    photo={block.photo}
                    alt={photoAlt(block.photo.number)}
                  />
                ) : (
                  <GuideMarkdown
                    key={`md-${i}`}
                    source={block.source}
                    offset={block.offset}
                    full={careGuide.care_guide_md}
                    idsByLine={headingIds}
                  />
                ))) : (
                  <GuideMarkdown
                    source={careGuide.care_guide_md}
                    full={careGuide.care_guide_md}
                    idsByLine={headingIds}
                  />
                )}
              </section>
            )}
          </div>

          <div className="col-lg-4">
            <div className="sticky-top" style={{ top: '2rem' }}>
              {/* Featured product card in right column */}
              {featured && (
                <div className="mb-3">
                  <h3 className="h5 mb-3">Shop {speciesDisplayName}</h3>
                  <ProductCard product={featured} hideCareGuideLink compact />
                </div>
              )}

              {/* Hero image (only when no featured card). S2-01.4b:
                  species gallery carousel when the care-guide payload
                  carries >1 image (per-slide credits render inside);
                  otherwise the original single hero + caption credit. */}
              {!featured && (Array.isArray(careGuide?.gallery) && careGuide.gallery.length > 1 ? (
                <div className="species-hero-image-container mb-3">
                  <PhotoCarousel
                    images={careGuide.gallery.map((g) => ({
                      // #99 r-review: slides are eager now — the hero
                      // fetches the 1200px display rendition, not the
                      // original camera upload.
                      url: buildAssetUrl(g?.display_url || g?.url),
                      thumbUrl: buildAssetUrl(g?.thumb_url || g?.url),
                      credit: (g?.credit || '').trim() || null,
                    })).filter((g) => g.url)}
                    alt={commonName ? `${commonName} (${speciesDisplayName})` : speciesDisplayName}
                    imgClassName="species-hero-image"
                    // 2026-07-30 operator: care-guide hero auto-advances
                    // like the product hero (single focal hero — no grid
                    // chaos concern here).
                  />
                </div>
              ) : (
                <div className="species-hero mb-3">
                  <div className="species-hero-image-container" style={{ position: 'relative' }}>
                    <img
                      src={speciesImageUrl || NO_PHOTO_SRC}
                      data-no-photo={speciesImageUrl ? undefined : '1'}
                      alt={commonName ? `${commonName} (${speciesDisplayName})` : speciesDisplayName}
                      className="species-hero-image"
                      onError={retryThenPlaceholder}
                    />
                  </div>
                  {/* CC-BY attribution for external photos. Renders
                      a <figcaption>-style credit below the hero so
                      it's clearly associated with the image. (P-08-
                      Images-Phase-2 S-08i2b.) It sits OUTSIDE the
                      image box (2026-10-12): that box has a fixed
                      height and overflow hidden, so a credit inside it
                      was clipped away — a CC BY credit nobody could
                      read. */}
                  <ImageAttribution
                    asset={resolveCredit(species)}
                    variant="caption"
                    className="species-hero-credit"
                  />
                </div>
              ))}

              {/* Table of Contents */}
              {toc.length > 0 && (
                <div className="card">
                  <div className="card-header">
                    <h5 className="mb-0">Table of Contents</h5>
                  </div>
                  <div className="card-body">
                    <nav className="toc">
                      {toc.map((heading, index) => (
                        <a
                          key={index}
                          href={`#${heading.id}`}
                          onClick={aimTocTarget}
                          className="d-block text-decoration-none py-1"
                          style={{
                            paddingLeft: `${(heading.level - 1) * 0.75}rem`,
                            fontSize: heading.level === 1 ? '0.9rem' : '0.85rem'
                          }}
                        >
                          {heading.text}
                        </a>
                      ))}
                    </nav>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Similar Products Section */}
        {genusOthers.length > 0 && (
          <section id="related-frogs" className="mt-5 mb-4">
            <h2 className="h4 mb-3">Shop other {species?.genus}</h2>
            <div className="row">
              {genusOthers.map((product) => (
                <div key={product.id} className="col-12 col-sm-6 col-md-4 mb-3">
                  <ProductCard product={product} hideCareGuideLink compact />
                </div>
              ))}
            </div>
          </section>
        )}

        <footer className="mt-4 pt-4 border-top">
          <div className="row">
            <div className="col-md-6">
              {/* R5 B5: a way back is a secondary, not the page's primary */}
              <button
                type="button"
                className="ff-btn ff-btn--secondary"
                onClick={() => navigate('/care-guides')}
              >
                ← Back to All Care Guides
              </button>
            </div>
            <div className="col-md-6 text-md-end">
              <small className="text-muted">
                Last updated: {new Date(careGuide.updated_at).toLocaleDateString()}
              </small>
            </div>
          </div>
        </footer>
      </div>

      <style jsx>{`
        .care-guide-content {
          line-height: 1.6;
        }

        .care-guide-content h1,
        .care-guide-content h2,
        .care-guide-content h3,
        .care-guide-content h4,
        .care-guide-content h5,
        .care-guide-content h6 {
          margin-top: 2rem;
          margin-bottom: 1rem;
        }

        /* Every Table of Contents target lands BELOW the sticky navbar
           (Cowork run 44). A TOC click sets the exact margin on its target
           (guideAnchorMargin — the bar while it is still pinned there,
           the gap alone once it has scrolled away), and so does a direct
           hash load once the guide has rendered (scrollToGuideHash). These
           defaults, the bar's known heights plus the 16px gap, are only
           the fallback for a jump nothing measured first — back/forward to
           a hash, a link inside the guide's own body — and are right only
           while the bar is still pinned. --ff-navbar-height is defined
           nowhere. */
        .care-guide-page {
          --ff-guide-anchor-offset: 113px;
        }

        @media (max-width: 991.98px) {
          .care-guide-page {
            --ff-guide-anchor-offset: 135px;
          }
        }

        .care-guide-page [id] {
          scroll-margin-top: var(--ff-guide-anchor-offset);
        }

        .care-guide-content h1:first-child,
        .care-guide-content h2:first-child,
        .care-guide-content h3:first-child {
          margin-top: 0;
        }

        .care-guide-content img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin: 1rem 0;
        }

        /* Photos between the sections of a long guide: the full column,
           rounded like the hero, a fixed 3:2 box so nothing shifts while
           a lazy photo loads. */
        .care-guide-content .care-guide-photo {
          margin: var(--ff-space-3xl, 32px) 0;
        }

        .care-guide-content .care-guide-photo__img {
          display: block;
          width: 100%;
          height: auto;
          aspect-ratio: 3 / 2;
          object-fit: cover;
          margin: 0;
          border-radius: var(--ff-radius-lg, 8px);
          box-shadow: var(--ff-shadow-md, 0 4px 8px rgba(0, 0, 0, 0.1));
          background-color: var(--ff-surface-tertiary, #e9ecef);
        }

        /* The credit follows the guide's own text colour (muted by
           opacity), not --ff-text-secondary: under data-theme="dark" that
           token turns near-white while this page keeps its light ground,
           and the credit vanished (measured in a real browser). The
           ImageAttribution caption sets its colour inline, hence the
           !important — scoped to this one figure. */
        .care-guide-content .care-guide-photo__credit {
          margin-top: var(--ff-space-sm, 8px);
          color: inherit;
          opacity: 0.75;
          font-size: 0.75rem;
          line-height: 1.3;
        }

        .care-guide-content .care-guide-photo__credit .ff-image-attribution {
          color: inherit !important;
          margin-top: 0 !important;
        }

        .care-guide-content blockquote {
          border-left: 4px solid var(--ff-brandBlue);
          padding-left: 1rem;
          margin: 1.5rem 0;
          font-style: italic;
          background: var(--ff-surface-secondary, #f8f9fa);
          padding: 1rem;
          border-radius: 4px;
        }

        .care-guide-content code {
          background: var(--ff-surface-tertiary, #e9ecef);
          padding: 0.125rem 0.25rem;
          border-radius: 3px;
          font-size: 0.9em;
        }

        .care-guide-content pre {
          background: var(--ff-surface-tertiary, #e9ecef);
          padding: 1rem;
          border-radius: 8px;
          overflow-x: auto;
        }

        .care-guide-content pre code {
          background: none;
          padding: 0;
        }

        /* The page's own text colour, muted. --ff-text-secondary turned
           near-white (#e5e7eb) on this white card under data-theme="dark"
           (measured 2026-10-12), and the old cyan hover is unreadable on
           white in either theme; hover now firms the link up instead. */
        .toc a {
          color: inherit;
          opacity: 0.8;
          transition: opacity 0.2s ease;
        }

        .toc a:hover,
        .toc a:focus-visible {
          opacity: 1;
          text-decoration: underline !important;
        }

        /* The hero's CC credit, under the photo: the page's own text
           colour, muted — ImageAttribution's inline --ff-text-secondary
           turns near-white under data-theme="dark" while this page keeps
           its light ground. Scoped here; ImageAttribution is unchanged. */
        .species-hero .species-hero-credit {
          color: inherit !important;
          opacity: 0.75;
          margin-top: var(--ff-space-sm, 8px) !important;
        }

        .species-hero-image-container {
          width: 100%;
          height: 200px;
          border-radius: var(--ff-radius-lg, 8px);
          overflow: hidden;
          box-shadow: var(--ff-shadow-md, 0 4px 8px rgba(0, 0, 0, 0.1));
          background-color: var(--ff-surface-tertiary, #e9ecef);
        }

        .species-hero-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        .species-hero-image:hover {
          transform: scale(1.02);
        }

        /* Batch 34: the no-photo tile is drawn WHOLE on a pale ground
           (contain, never a cropped cover) and does not zoom. */
        .species-hero-image[data-no-photo] {
          object-fit: contain;
          background: var(--ff-surface-secondary, #f8f9fa);
        }

        .species-hero-image[data-no-photo]:hover {
          transform: none;
        }

        @media (max-width: 991.98px) {
          .species-hero-image-container {
            height: 150px;
            margin-top: var(--ff-space-md, 16px);
          }
        }
      `}</style>
    </>
  );
};

export default SpeciesCareGuide;